import { createReducer, getType } from "typesafe-actions";

import { Actions as AppActions, initializeApp } from "@app/store/app/actions";
import AlertBadgesState from "@app/store/mapLayers/alerts/AlertBadgesState";
import HeatMapLayerData from "@app/store/mapLayers/heatMap/HeatMapLayerData";

import {
  AlertBadgesActions,
  BoundariesActions,
  HeatMapActions,
  MapLayersActions,
  toggleLayerLegendExpandedState,
  toggleLayerVisibility,
  UserLocationActions,
} from "./actions";
import alertBadgesLayer, { alertBadgesInitialState } from "./alerts/reducers";
import boundariesLayer, { boundariesLayerInitialState } from "./boundaries/reducers";
import BoundariesLayerData from "./boundaries/types/BoundariesLayerData";
import heatMapLayer, { heatMapLayerInitialState } from "./heatMap/reducers";
import { TrafficActions } from "./traffic/actions";
import trafficLayer, { trafficInitialState } from "./traffic/reducers";
import TrafficLayerData from "./traffic/TrafficLayerData";
import userLocationsLayer, { userLocationInitialState } from "./userLocations/reducers";
import UserLocationsState from "./userLocations/types/UserLocationsState";

export interface LayersState {
  alerts: AlertBadgesState;
  boundaries: BoundariesLayerData;
  heatMap: HeatMapLayerData;
  traffic: TrafficLayerData;
  userLocations: UserLocationsState;
}

export type MapLayer = keyof LayersState;

export const layersInitialState: LayersState = {
  alerts: alertBadgesInitialState,
  boundaries: boundariesLayerInitialState,
  heatMap: heatMapLayerInitialState,
  traffic: trafficInitialState,
  userLocations: userLocationInitialState,
};

const layers = createReducer<LayersState, AppActions | MapLayersActions>(layersInitialState)
  .handleType(getType(toggleLayerVisibility), (state, { payload }) => ({
    ...state,
    [payload.name]: {
      ...state[payload.name],
      visible: payload.visible,
    },
  }))
  .handleType(getType(toggleLayerLegendExpandedState), (state, { payload }) => ({
    ...state,
    [payload.name]: {
      ...state[payload.name],
      legendExpanded: payload.expanded,
    },
  }))
  .handleType(
    Object.keys(heatMapLayer.handlers) as HeatMapActions["type"][],
    (state: LayersState, action: HeatMapActions) => ({
      ...state,
      heatMap: heatMapLayer(state.heatMap, action),
    })
  )
  .handleType(
    Object.keys(alertBadgesLayer.handlers) as AlertBadgesActions["type"][],
    (state: LayersState, action: AlertBadgesActions) => ({
      ...state,
      alerts: alertBadgesLayer(state.alerts, action),
    })
  )
  .handleType(
    Object.keys(boundariesLayer.handlers) as BoundariesActions["type"][],
    (state: LayersState, action: BoundariesActions) => ({
      ...state,
      boundaries: boundariesLayer(state.boundaries, action),
    })
  )
  .handleType(
    Object.keys(userLocationsLayer.handlers) as UserLocationActions["type"][],
    (state: LayersState, action: UserLocationActions) => ({
      ...state,
      userLocations: userLocationsLayer(state.userLocations, action),
    })
  )
  .handleType(
    Object.keys(trafficLayer.handlers) as TrafficActions["type"][],
    (state: LayersState, action: TrafficActions) => ({
      ...state,
      traffic: trafficLayer(state.traffic, action),
    })
  )
  // initializeApp.success is handled by alerts, boundaries, heatMap, and traffic sub-reducers
  // for metadata hydration. The Object.keys(sub.handlers)-based routing above registers each
  // sub-reducer's dispatcher for the SAME action type, and the last registration overwrites
  // the prior ones — so only one sub-reducer's hydration ever ran. Explicitly fan the action
  // out to every hydrating sub-reducer here, after all the per-slice routes, so this handler
  // wins the type-keyed lookup and each slice gets a chance to merge its persisted settings.
  .handleType(getType(initializeApp.success), (state, action) => ({
    ...state,
    alerts: alertBadgesLayer(state.alerts, action),
    boundaries: boundariesLayer(state.boundaries, action),
    heatMap: heatMapLayer(state.heatMap, action),
    traffic: trafficLayer(state.traffic, action),
  }));

export default layers;
